/**
 * Did anyone touch the page? One `page_touch` event per session, sent on the
 * first tap, click, key, scroll or wheel.
 *
 * Owner, 2026-11-04: "never touched the page" is a hint that a browser is a
 * robot, never proof. It only ever feeds the "unscored, robot-like" sub-band
 * of the people (utils/clickhouse/people-sql.ts) and never takes anyone out of
 * a people number. Someone who reads a listing and leaves without scrolling is
 * a person.
 *
 * The lightest version: one listener set, removed after the first touch, and
 * nothing sent at all for a session that already reported one. A browser that
 * never touched sends nothing; the reader knows the page could have reported
 * because the same load sent a `script_health` row of probe v3 or later
 * (utils/script-health.ts), so old pages do not read as untouched.
 */
import { getAnalytics } from './analytics';
import { getSessionId } from './tracking-context';
import { PAGE_TOUCH_EVENT, type PageTouchVia } from './page-touch-event';

const TOUCHED_KEY = 'sw_touched_session';

/** DOM event -> the `via` it is reported as. */
const LISTENED: readonly [string, PageTouchVia][] = [
  ['pointerdown', 'pointer'],
  ['keydown', 'key'],
  ['scroll', 'scroll'],
  ['wheel', 'wheel'],
  ['touchstart', 'touch'],
];

let installed = false;
/** Mirror of the stored mark, for browsers that refuse sessionStorage. */
let touchedInMemory = '';
let remove: (() => void) | null = null;

function readTouched(): string {
  try {
    return sessionStorage.getItem(TOUCHED_KEY) || touchedInMemory;
  // eslint-disable-next-line sellwild-code/catch-must-log -- storage refused (privacy mode); the in-memory mark answers instead
  } catch {
    return touchedInMemory;
  }
}

function markTouched(sessionId: string): void {
  touchedInMemory = sessionId;
  try {
    sessionStorage.setItem(TOUCHED_KEY, sessionId);
  // eslint-disable-next-line sellwild-code/catch-must-log -- storage refused; the in-memory mark holds until a reload
  } catch {
    // Nothing to do.
  }
}

function onFirstTouch(via: PageTouchVia): void {
  remove?.();
  remove = null;
  const sessionId = getSessionId();
  if (!sessionId || readTouched() === sessionId) return;
  markTouched(sessionId);
  getAnalytics()?.track({ event_type: PAGE_TOUCH_EVENT, properties: { via } });
}

/** Called once from pages/_app.tsx, only in a browser. */
export function installPageTouch(): void {
  if (installed) return;
  installed = true;
  const sessionId = getSessionId();
  if (sessionId && readTouched() === sessionId) return;

  const handlers = LISTENED.map(([type, via]) => {
    const handler = () => onFirstTouch(via);
    // Capture, so a scroll inside a panel counts too; passive, so it never slows one.
    window.addEventListener(type, handler, { capture: true, passive: true });
    return [type, handler] as const;
  });
  remove = () => {
    for (const [type, handler] of handlers) window.removeEventListener(type, handler, { capture: true });
  };
}

/** Test seam: forget the install and the marks. */
export function resetPageTouch(): void {
  remove?.();
  remove = null;
  installed = false;
  touchedInMemory = '';
}
